import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Crown, AlertTriangle, TrendingUp, Users, HardDrive } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';

const SubscriptionWidget = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription-tiers/clinic/current`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscription || !subscription.tier) {
    return null; // Don't show widget if no subscription
  }

  const { tier, usage } = subscription;
  
  const getUsagePercent = (current, max) => {
    if (max === -1) return 0;
    return (current / max) * 100;
  };

  const isOverLimit = (current, max) => {
    if (max === -1) return false;
    return current > max;
  };

  const isNearLimit = (current, max) => {
    if (max === -1) return false;
    return getUsagePercent(current, max) >= 80 && !isOverLimit(current, max);
  };

  const hasWarning = isOverLimit(usage.current_users, tier.max_users) || 
                     isOverLimit(usage.current_members, tier.max_members) ||
                     isNearLimit(usage.current_users, tier.max_users) ||
                     isNearLimit(usage.current_members, tier.max_members);

  return (
    <Card className={`${hasWarning ? 'border-orange-300 bg-orange-50' : 'border-teal-300 bg-teal-50'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className={`h-5 w-5 ${hasWarning ? 'text-orange-600' : 'text-teal-600'}`} />
            <CardTitle className="text-base">{tier.name} Plan</CardTitle>
          </div>
          <Badge className={hasWarning ? 'bg-orange-600' : 'bg-teal-600'}>
            ${tier.monthly_price}/mo
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Usage Indicators */}
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-700 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Members
              </span>
              <span className={`font-semibold ${isOverLimit(usage.current_members, tier.max_members) ? 'text-red-600' : isNearLimit(usage.current_members, tier.max_members) ? 'text-orange-600' : 'text-gray-700'}`}>
                {usage.current_members}/{tier.max_members === -1 ? '∞' : tier.max_members}
              </span>
            </div>
            {tier.max_members !== -1 && (
              <Progress 
                value={getUsagePercent(usage.current_members, tier.max_members)} 
                className={`h-1.5 ${isOverLimit(usage.current_members, tier.max_members) ? 'bg-red-200' : isNearLimit(usage.current_members, tier.max_members) ? 'bg-orange-200' : ''}`}
              />
            )}
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-700 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Users
              </span>
              <span className={`font-semibold ${isOverLimit(usage.current_users, tier.max_users) ? 'text-red-600' : isNearLimit(usage.current_users, tier.max_users) ? 'text-orange-600' : 'text-gray-700'}`}>
                {usage.current_users}/{tier.max_users === -1 ? '∞' : tier.max_users}
              </span>
            </div>
            {tier.max_users !== -1 && (
              <Progress 
                value={Math.min(getUsagePercent(usage.current_users, tier.max_users), 100)} 
                className={`h-1.5 ${isOverLimit(usage.current_users, tier.max_users) ? 'bg-red-200' : isNearLimit(usage.current_users, tier.max_users) ? 'bg-orange-200' : ''}`}
              />
            )}
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-700 flex items-center gap-1">
                <HardDrive className="h-3 w-3" />
                Storage
              </span>
              <span className="font-semibold text-gray-700">
                {usage.current_storage_gb?.toFixed(1)}GB/{tier.max_storage_gb}GB
              </span>
            </div>
            <Progress value={getUsagePercent(usage.current_storage_gb, tier.max_storage_gb)} className="h-1.5" />
          </div>
        </div>

        {/* Warning Message */}
        {hasWarning && (
          <div className="flex items-start gap-2 p-2 bg-orange-100 border border-orange-300 rounded text-xs">
            <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-orange-900">
                {isOverLimit(usage.current_users, tier.max_users) || isOverLimit(usage.current_members, tier.max_members) 
                  ? 'Over Limit!' 
                  : 'Approaching Limit'}
              </p>
              <p className="text-orange-700">
                {isOverLimit(usage.current_users, tier.max_users) || isOverLimit(usage.current_members, tier.max_members)
                  ? 'Please upgrade to continue adding.'
                  : 'Consider upgrading soon.'}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => navigate('/admin/subscription')}
            className="flex-1 text-xs"
          >
            View Details
          </Button>
          {hasWarning && (
            <Button 
              size="sm" 
              onClick={() => navigate('/admin/subscription')}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-xs"
            >
              <TrendingUp className="h-3 w-3 mr-1" />
              Upgrade
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionWidget;
